import { useEffect, useRef } from 'react';
import { detectOvernight, validateOperatingSchedule } from '../../domain/schedule.js';
import type { IsoWeekday, OperatingSchedule, WorldDraft } from '../../domain/types.js';
import { OPERATING_SCHEDULE_SCHEMA_VERSION } from '../../domain/types.js';
import { CLOSED_BEHAVIOR_OPTIONS } from '../displayLabel.js';
import { defaultSchedule } from './fallbacks.js';
import { scheduleWithLocationTimezone } from './scheduleTimezone.js';
import { useLocationTimezoneResolution } from './useLocationTimezoneResolution.js';
import './createWorldSteps.css';

export const SCHEDULE_FIELD_IDS = {
  timezone: 'schedule.timezone',
  days: 'schedule.days',
  openTime: 'schedule.openTime',
  closeTime: 'schedule.closeTime',
} as const;

const WEEKDAYS: { d: IsoWeekday; label: string }[] = [
  { d: 1, label: 'Mon' }, { d: 2, label: 'Tue' }, { d: 3, label: 'Wed' },
  { d: 4, label: 'Thu' }, { d: 5, label: 'Fri' }, { d: 6, label: 'Sat' }, { d: 7, label: 'Sun' },
];

export interface ScheduleValidationIssue {
  fieldId: string;
  message: string;
}

export function scheduleFieldValidationIssues(
  schedule: OperatingSchedule | null,
  _locationRegion?: string,
): ScheduleValidationIssue[] {
  if (!schedule) {
    return [{ fieldId: SCHEDULE_FIELD_IDS.days, message: 'Operating schedule is required.' }];
  }

  const issues: ScheduleValidationIssue[] = [];

  if (schedule.timezoneStrategy === 'explicit' && !schedule.timezone?.trim()) {
    issues.push({
      fieldId: SCHEDULE_FIELD_IDS.timezone,
      message: 'Explicit timezone strategy requires an IANA timezone id.',
    });
  }

  if (schedule.timezoneStrategy === 'location' && !schedule.timezone?.trim()) {
    issues.push({
      fieldId: SCHEDULE_FIELD_IDS.timezone,
      message: 'Could not determine a timezone from your location. Enter an IANA timezone instead (or fix city, region, country).',
    });
  }

  if (!schedule.hasOperatingHours) {
    return issues;
  }

  const result = validateOperatingSchedule(schedule);
  for (const err of result.errors) {
    if (err.includes('timezone')) {
      issues.push({ fieldId: SCHEDULE_FIELD_IDS.timezone, message: err });
    } else if (err.includes('day')) {
      issues.push({ fieldId: SCHEDULE_FIELD_IDS.days, message: err });
    } else if (err.includes('openTime')) {
      issues.push({ fieldId: SCHEDULE_FIELD_IDS.openTime, message: err });
    } else if (err.includes('closeTime')) {
      issues.push({ fieldId: SCHEDULE_FIELD_IDS.closeTime, message: err });
    } else if (issues.length === 0) {
      issues.push({ fieldId: SCHEDULE_FIELD_IDS.days, message: err });
    }
  }

  return issues;
}

export function validateScheduleStep(
  schedule: OperatingSchedule | null,
  locationRegion?: string,
): string | null {
  return scheduleFieldValidationIssues(schedule, locationRegion)[0]?.message ?? null;
}

export function firstIncompleteScheduleField(
  schedule: OperatingSchedule | null,
  locationRegion?: string,
): string | null {
  return scheduleFieldValidationIssues(schedule, locationRegion)[0]?.fieldId ?? null;
}

export function OperatingHoursStep(props: {
  schedule: OperatingSchedule;
  locationRegion?: string;
  draftId: string;
  onChange: (s: OperatingSchedule) => void;
  onLocationCorrected?: (region: string) => void;
  onScheduleUpdated?: (schedule: OperatingSchedule) => void;
  focusFieldId?: string | null;
}): JSX.Element {
  const base = props.schedule;
  const firstInvalid = props.focusFieldId ?? firstIncompleteScheduleField(base, props.locationRegion);
  const tzRef = useRef<HTMLInputElement>(null);
  const openRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLInputElement>(null);
  const daysRef = useRef<HTMLDivElement>(null);
  const timezoneStatus = useLocationTimezoneResolution({
    locationRegion: props.locationRegion,
    schedule: base,
    onLocationCorrected: (region) => props.onLocationCorrected?.(region),
    onScheduleUpdated: (schedule) => props.onScheduleUpdated?.(schedule),
  });

  useEffect(() => {
    if (!props.focusFieldId) return;
    const map: Record<string, { current: HTMLElement | null }> = {
      [SCHEDULE_FIELD_IDS.timezone]: tzRef,
      [SCHEDULE_FIELD_IDS.openTime]: openRef,
      [SCHEDULE_FIELD_IDS.closeTime]: closeRef,
      [SCHEDULE_FIELD_IDS.days]: daysRef,
    };
    map[props.focusFieldId]?.current?.focus();
  }, [props.focusFieldId]);

  const patch = (p: Partial<OperatingSchedule>): void => {
    let next = { ...base, ...p, schemaVersion: OPERATING_SCHEDULE_SCHEMA_VERSION };
    if (p.openTime || p.closeTime) {
      next.overnight = detectOvernight(next.openTime, next.closeTime);
    }
    if (next.timezoneStrategy === 'location' || p.timezoneStrategy === 'location') {
      next = scheduleWithLocationTimezone(next, props.locationRegion);
    }
    props.onChange(next);
  };

  const live247 = !base.hasOperatingHours;

  return (
    <div className="world-form">
      <p className="world-step-subtitle">Operating/Working hours and rhythm</p>
      <label className="world-check">
        <input
          type="checkbox"
          checked={live247}
          onChange={(e) => patch({ hasOperatingHours: !e.target.checked })}
        />
        This location is live 24/7
      </label>

      <fieldset className="world-field-row">
        <legend>Timezone strategy</legend>
        <div className="world-radio-group world-radio-group--hours" role="radiogroup" aria-label="Timezone strategy">
          <label className={`world-radio-option ${base.timezoneStrategy === 'location' ? 'sel' : ''}`}>
            <input
              type="radio"
              name="tz-strategy"
              checked={base.timezoneStrategy === 'location'}
              onChange={() => patch({ timezoneStrategy: 'location' })}
            />
            <span>Base the timezone on the location of your world.</span>
          </label>
          <label className={`world-radio-option ${base.timezoneStrategy === 'explicit' ? 'sel' : ''}`}>
            <input
              type="radio"
              name="tz-strategy"
              checked={base.timezoneStrategy === 'explicit'}
              onChange={() => patch({ timezoneStrategy: 'explicit' })}
            />
            <span>Use explicit world timezone</span>
          </label>
        </div>
      </fieldset>

      {base.timezoneStrategy === 'explicit' && (
        <div
          className={`world-field-row ${firstInvalid === SCHEDULE_FIELD_IDS.timezone ? 'world-field-invalid' : ''}`}
        >
          <label htmlFor="tz-name">IANA timezone</label>
          <input
            id="tz-name"
            ref={tzRef}
            placeholder="America/Los_Angeles"
            value={base.timezone ?? ''}
            onChange={(e) => patch({ timezone: e.target.value })}
            aria-invalid={firstInvalid === SCHEDULE_FIELD_IDS.timezone}
          />
        </div>
      )}

      {base.timezoneStrategy === 'location' && timezoneStatus.phase === 'pending' && (
        <p className="world-muted" role="status">Finding the timezone for this location…</p>
      )}
      {base.timezoneStrategy === 'location' && timezoneStatus.phase === 'done' && base.timezone && (
        <p className="world-muted" role="status">Timezone: {base.timezone}</p>
      )}
      {base.timezoneStrategy === 'location' && firstInvalid === SCHEDULE_FIELD_IDS.timezone && (
        <p className="world-field-invalid" role="alert">
          Could not determine a timezone from your location. Enter an IANA timezone instead (or fix city, region, country).
        </p>
      )}

      {base.hasOperatingHours && (
        <>
          <div
            ref={daysRef}
            tabIndex={-1}
            className={`world-day-row ${firstInvalid === SCHEDULE_FIELD_IDS.days ? 'world-field-invalid' : ''}`}
          >
            <span className="world-days-label">Days of operations:</span>
            {WEEKDAYS.map(({ d, label }) => (
              <label key={d} className="world-day">
                <input
                  type="checkbox"
                  checked={base.daysOpen.includes(d)}
                  onChange={(e) => {
                    const days = e.target.checked
                      ? [...base.daysOpen, d].sort()
                      : base.daysOpen.filter((x) => x !== d);
                    patch({ daysOpen: days as IsoWeekday[] });
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          <div
            className={`world-field-row ${firstInvalid === SCHEDULE_FIELD_IDS.openTime ? 'world-field-invalid' : ''}`}
          >
            <label htmlFor="open-time">Opens</label>
            <input
              id="open-time"
              ref={openRef}
              type="time"
              value={base.openTime}
              onChange={(e) => patch({ openTime: e.target.value })}
              aria-invalid={firstInvalid === SCHEDULE_FIELD_IDS.openTime}
            />
          </div>
          <div
            className={`world-field-row ${firstInvalid === SCHEDULE_FIELD_IDS.closeTime ? 'world-field-invalid' : ''}`}
          >
            <label htmlFor="close-time">Closes</label>
            <input
              id="close-time"
              ref={closeRef}
              type="time"
              value={base.closeTime}
              onChange={(e) => patch({ closeTime: e.target.value })}
              aria-invalid={firstInvalid === SCHEDULE_FIELD_IDS.closeTime}
            />
          </div>
          {base.overnight && <p className="world-muted">Overnight schedule (closes after midnight).</p>}
          <div className="world-field-row">
            <label htmlFor="closed-behavior">While closed, characters</label>
            <select
              id="closed-behavior"
              value={base.closedBehavior}
              onChange={(e) => patch({ closedBehavior: e.target.value as OperatingSchedule['closedBehavior'] })}
            >
              {CLOSED_BEHAVIOR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
}

export function OperatingHoursStepInit(props: {
  draft: WorldDraft;
  onInit: (schedule: OperatingSchedule) => void;
  children: (schedule: OperatingSchedule) => JSX.Element;
}): JSX.Element {
  const schedule = props.draft.schedule ?? defaultSchedule(null, props.draft.draftId);
  useEffect(() => {
    if (!props.draft.schedule) {
      props.onInit(defaultSchedule(null, props.draft.draftId));
    }
  }, [props.draft.schedule, props.draft.draftId, props.onInit]);
  return props.children(schedule);
}
